// Dashboard — $-weighted rot leaderboard + deals-scored table with filters.
//
// Phase 10 endpoints:
//   GET /api/v1/leaderboard — top-10 rotted deals by amount_minor DESC
//   GET /api/v1/deals — paginated deals with stage/is_rotting filters

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Badge } from '@avagency/frontend/components/ui/badge';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import {
  AlertTriangle,
  TrendingDown,
  DollarSign,
  Clock,
} from 'lucide-react';

import {
  fetchLeaderboard,
  fetchDeals,
  type LeaderboardItem,
  type DealScored,
  type DealsQuery,
} from '../api';

const STAGES = ['Prospect', 'Discovery', 'Demo', 'Proposal', 'Closing'];
const PAGE_SIZE = 20;

function fmtAmount(minor: number, currency: string): string {
  const major = minor / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  }).format(major);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function SeverityBadge({ severity }: { severity: string }) {
  const cls =
    severity === 'critical'
      ? 'severity-critical'
      : severity === 'high'
      ? 'severity-high'
      : severity === 'medium'
      ? 'severity-medium'
      : 'severity-low';
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`}>
      {severity}
    </span>
  );
}

function LeaderboardRow({ item, rank }: { item: LeaderboardItem; rank: number }) {
  return (
    <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
      <td className="py-3 px-4 text-sm text-muted-foreground w-8">{rank}</td>
      <td className="py-3 px-4">
        <div className="text-sm font-medium text-foreground truncate max-w-[180px]">
          {item.external_id}
        </div>
        <div className="text-xs text-muted-foreground">{item.owner_name || item.owner_email}</div>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <DollarSign size={14} className="text-muted-foreground" />
          {fmtAmount(item.amount_minor, item.currency)}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{item.stage}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-sm text-orange-400">
          <Clock size={13} />
          {item.stage_age_days}d
        </div>
      </td>
      <td className="py-3 px-4">
        <SeverityBadge severity={item.severity} />
      </td>
    </tr>
  );
}

function DealsTableRow({ deal }: { deal: DealScored }) {
  return (
    <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
      <td className="py-3 px-4">
        <div className="text-sm font-medium text-foreground truncate max-w-[160px]">{deal.external_id}</div>
        <div className="text-xs text-muted-foreground">{deal.owner_email}</div>
      </td>
      <td className="py-3 px-4 text-sm text-foreground">{deal.stage}</td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{fmtAmount(deal.amount_minor, deal.currency)}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-sm text-orange-400">
          <Clock size={13} />
          {deal.stage_age_days}d
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{deal.last_activity_age_days}d ago</td>
      <td className="py-3 px-4">
        {deal.is_rotting ? (
          <span className="inline-flex items-center gap-1 text-xs text-red-400">
            <TrendingDown size={12} /> Rotting
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">OK</span>
        )}
      </td>
      <td className="py-3 px-4">
        <SeverityBadge severity={deal.severity} />
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground">{fmtDate(deal.scanned_at)}</td>
    </tr>
  );
}

export interface DashboardProps {
  integrationID: string;
  initialTab?: 'leaderboard' | 'deals';
}

export function Dashboard({ initialTab = 'leaderboard' }: DashboardProps) {
  const [tab, setTab] = useState<'leaderboard' | 'deals'>(initialTab);

  // Leaderboard query
  const leaderboard = useQuery({
    queryKey: ['pipeline-rot', 'leaderboard'],
    queryFn: fetchLeaderboard,
  });

  // Deals query with filters
  const [filters, setFilters] = useState<DealsQuery>({ is_rotting: true, page: 1, page_size: PAGE_SIZE });
  const deals = useQuery({
    queryKey: ['pipeline-rot', 'deals', filters],
    queryFn: () => fetchDeals(filters),
    enabled: tab === 'deals',
  });

  const totalRottingAmount =
    leaderboard.data?.leaderboard.reduce((sum, i) => sum + i.amount_minor, 0) ?? 0;

  return (
    <div className="p-6 space-y-6">
      {/* Summary stats */}
      {leaderboard.data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-red-400" /> Rotting deals
              </div>
              <div className="text-2xl font-bold text-foreground">
                {leaderboard.data.leaderboard.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <DollarSign size={12} className="text-orange-400" /> $ at risk
              </div>
              <div className="text-2xl font-bold text-foreground">
                {fmtAmount(totalRottingAmount, 'USD')}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <TrendingDown size={12} className="text-yellow-400" /> Avg stage age
              </div>
              <div className="text-2xl font-bold text-foreground">
                {leaderboard.data.leaderboard.length > 0
                  ? Math.round(
                      leaderboard.data.leaderboard.reduce((s, i) => s + i.stage_age_days, 0) /
                        leaderboard.data.leaderboard.length
                    )
                  : 0}
                d
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2">
        <Button
          variant={tab === 'leaderboard' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('leaderboard')}
        >
          Rot Leaderboard
        </Button>
        <Button
          variant={tab === 'deals' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('deals')}
        >
          All Deals
        </Button>
      </div>

      {/* Leaderboard tab */}
      {tab === 'leaderboard' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-400" />
              Top 10 Rotting Deals by Value
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {leaderboard.isLoading && (
              <div className="space-y-3 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
            {leaderboard.isError && (
              <div className="p-6 text-sm text-destructive">
                Failed to load leaderboard. {(leaderboard.error as Error).message}
              </div>
            )}
            {leaderboard.data && leaderboard.data.leaderboard.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No rotting deals found. Good shape.
              </div>
            )}
            {leaderboard.data && leaderboard.data.leaderboard.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border/40 text-xs text-muted-foreground">
                      <th className="py-2 px-4">#</th>
                      <th className="py-2 px-4">Deal</th>
                      <th className="py-2 px-4">Value</th>
                      <th className="py-2 px-4">Stage</th>
                      <th className="py-2 px-4">Age</th>
                      <th className="py-2 px-4">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.data.leaderboard.map((item, i) => (
                      <LeaderboardRow key={item.id} item={item} rank={i + 1} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deals tab with filters */}
      {tab === 'deals' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center gap-3">
              <CardTitle className="text-base">All Scored Deals</CardTitle>
              {/* Stage filter */}
              <select
                className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground"
                value={filters.stage ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, stage: e.target.value || undefined, page: 1 }))
                }
              >
                <option value="">All stages</option>
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {/* Rotting filter */}
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.is_rotting ?? false}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, is_rotting: e.target.checked || undefined, page: 1 }))
                  }
                  className="rounded"
                />
                Rotting only
              </label>
              {/* Badge showing active count */}
              {deals.data && (
                <Badge variant="secondary" className="ml-auto">
                  {deals.data.total} deals
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {deals.isLoading && (
              <div className="space-y-3 p-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
            {deals.isError && (
              <div className="p-6 text-sm text-destructive">
                Failed to load deals. {(deals.error as Error).message}
              </div>
            )}
            {deals.data && deals.data.deals.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">No deals match the current filters.</div>
            )}
            {deals.data && deals.data.deals.length > 0 && (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border/40 text-xs text-muted-foreground">
                        <th className="py-2 px-4">Deal</th>
                        <th className="py-2 px-4">Stage</th>
                        <th className="py-2 px-4">Value</th>
                        <th className="py-2 px-4">Stage age</th>
                        <th className="py-2 px-4">Last activity</th>
                        <th className="py-2 px-4">Status</th>
                        <th className="py-2 px-4">Severity</th>
                        <th className="py-2 px-4">Scanned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deals.data.deals.map((d) => (
                        <DealsTableRow key={d.id} deal={d} />
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-border/40 px-4 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(filters.page ?? 1) <= 1}
                    onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Page {filters.page ?? 1} · {deals.data.total} total
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      (filters.page ?? 1) * PAGE_SIZE >= deals.data.total
                    }
                    onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
